import { FileWithPreview, processImage, ProcessImageErrorType } from 'components/fileuploader/ImageUploader2';
import { ImportingFileType } from './CancelImportImageButton';
import { useContext, useRef, useState } from 'react';
import { KeycloakContext } from 'components/Secured';
import { map, path } from 'ramda';
import IProduct from 'models/product';
import IProducer from 'models/producer';
import { isDefined } from 'utils/helpers';
import byteSize from 'byte-size';
import { downloadImageAsFile, getImageWithBGRemoved, uploadImages } from 'utils/images';
import { ExecutionResult } from 'graphql';
import { TSignedRequestResponse } from './FillProductByImage';

const useImportProductImages = ({
  state,
  producer,
  getS3Requests,
}: {
  state?: IProduct;
  producer?: IProducer;
  getS3Requests: (images: File[], isLargeFilesRequest?: boolean) => Promise<ExecutionResult<TSignedRequestResponse>>;
}) => {
  const keycloakCtx = useContext(KeycloakContext);
  const token = path(['keycloak', 'token'], keycloakCtx) as string;

  const importImagesRef = useRef({
    abortted: false,
    abortController: null as AbortController | null,
    allNewSmallImages: [] as File[],
  });
  const [isImportingImages, setIsImportingImages] = useState(false);
  const [importingFiles, setImportingFiles] = useState<ImportingFileType[]>([]);
  const [imageProcessErrors, setImageProccessErrors] = useState<ProcessImageErrorType[]>([]);
  const [failedAIRequest, setFailedAIRequest] = useState({
    error: false,
    message: '',
    imageName: '',
  });
  const [files, setFiles] = useState<File[]>([]);

  const onImportImages = async (filesParam: File[]) => {
    importImagesRef.current = { abortted: false, abortController: new AbortController(), allNewSmallImages: [] };

    setIsImportingImages(true);
    setImageProccessErrors([]);
    setFailedAIRequest({ error: false, message: '', imageName: '' });

    setImportingFiles(filesParam.map(file => ({ file, completed: false })));

    const filePathSet = new Set<string>();
    const allNewFiles: File[] = [];
    const aiErrors: { fileName: string; error: string }[] = [];

    await Promise.all(
      filesParam.map(async (rawFile, index) => {
        // Process image
        let { file: processedImage, error } = await processImage(
          rawFile,
          token,
          true,
          state,
          producer,
          importImagesRef.current.abortController?.signal
        );
        if (error) {
          setImageProccessErrors(oldState => [...oldState, error] as ProcessImageErrorType[]);
        }
        if (importImagesRef.current.abortted || !processedImage) return;

        const type = processedImage.type;
        const processedName = Date.now().toString() + '_' + processedImage.name;
        processedImage = new File([processedImage], processedName, { type }) as FileWithPreview;
        processedImage.path = processedName;

        if (filePathSet.has(processedImage.path)) return;
        filePathSet.add(processedImage.path);

        const imageSize = byteSize(processedImage.size);
        const { value, long } = imageSize;
        const isLargeImage = long === 'megabytes' && Number(value) > 11.9;
        const name = isDefined(processedImage.name) ? processedImage.name.split('.')[0] : '';

        const newImage = new File([processedImage], name, { type }) as FileWithPreview;
        newImage.path = name;

        let fileToRemoveBg = newImage;

        // Compress large image
        if (isLargeImage) {
          const largeFile = new File([newImage], `temporary_${index}`, { type });
          const { data } = await getS3Requests([largeFile], true);
          const signedRequest = data?.getS3SignRequest?.requests?.[0];

          if (importImagesRef.current.abortted || !signedRequest) return;

          try {
            await uploadImages([largeFile], [signedRequest], importImagesRef.current.abortController?.signal);
          } catch {
            aiErrors.push({
              error: `Image(s) may still be too large after compression.`,
              fileName: rawFile.name,
            });
            return;
          }

          if (importImagesRef.current.abortted) return;

          try {
            fileToRemoveBg = await downloadImageAsFile(
              `${signedRequest.pictureUrl}?quality=100&auto=compress,enhance`,
              name || signedRequest.fileName,
              type,
              importImagesRef.current.abortController?.signal
            );
          } catch {
            aiErrors.push({
              error: `Image(s) may still be too large after compression.`,
              fileName: rawFile.name,
            });
            return;
          }
        }
        if (importImagesRef.current.abortted) return;

        // Remove background
        const iBg = await getImageWithBGRemoved(
          fileToRemoveBg,
          token,
          10 * 60 * 1000,
          true,
          state,
          producer,
          importImagesRef.current.abortController?.signal
        );

        if (importImagesRef.current.abortted) return;

        allNewFiles.push(processedImage);
        if (iBg instanceof File) {
          allNewFiles.push(iBg);
        } else if (iBg.error) {
          aiErrors.push({ fileName: rawFile.name, error: iBg.error });
        }

        setImportingFiles(oldState => {
          return oldState.map(item => {
            if (item.file !== rawFile) return item;
            return { ...item, completed: true };
          });
        });
      })
    );

    if (!importImagesRef.current.abortted && aiErrors.length) {
      setFailedAIRequest({
        error: true,
        imageName: `${map(e => e.fileName, aiErrors)}`,
        message: `${map(e => e.error, aiErrors)}`,
      });
    }
    setFiles([...files, ...allNewFiles]);

    await getS3Requests([...files, ...allNewFiles]);
    setIsImportingImages(false);
  };

  return {
    importImagesRef,
    isImportingImages,
    failedAIRequest,
    imageProcessErrors,
    importingFiles,
    files,
    setFiles,
    setImportingFiles,
    onImportImages,
  };
};

export default useImportProductImages;
